import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import AdminTableDeleteButton from '../AdminTableDeleteButton';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import { isAdministrateur } from '../../../utils/adminAccess';
import { deleteAdminApprenant } from '../../../services/academiaAdminService';

type ApprenantRow = {
  id: string;
  telephone?: string;
  email?: string;
  dateCreation?: string;
  profil?: { prenom?: string; nom?: string; postnom?: string };
  profilApprenant?: { matricule?: string; categorie?: string };
  categorisationRaison?: string | null;
};

type Props = {
  user?: { role?: string } | null;
  items: ApprenantRow[];
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => void;
};

function formatName(row: ApprenantRow) {
  const p = row.profil;
  return [p?.prenom, p?.nom, p?.postnom].filter(Boolean).join(' ') || '—';
}

export default function AcademiaApprenantsPanel({ user, items, isLoading, isError, onRefresh }: Props) {
  useRegisterPortalRefresh(onRefresh);
  const canDelete = isAdministrateur(user);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { paginatedItems, currentPage, totalPages, onPageChange, summary } = useAdminTablePagination(items);

  const sideCard = useMemo(
    () => ({
      label: 'Apprenants actifs',
      value: isLoading ? '…' : String(items.length),
      helper: isLoading ? null : 'Au moins un acompte sur une formation',
      sideLinks: [{ label: 'Inscriptions', to: '/admin/inscriptions', icon: 'assignment' }],
    }),
    [isLoading, items.length],
  );

  return (
    <AdminPageShell
      title="Annuaire apprenants"
      sideCard={sideCard}
      description={
        isLoading || isError
          ? null
          : `${items.length} apprenant(s) ayant versé au moins un acompte sur le paiement d'une formation.`
      }
      onRetry={onRefresh}
      isLoading={isLoading}
      isError={isError}
      loadingMessage="Chargement des apprenants…"
      errorMessage="Impossible de charger les apprenants."
    >
      <div className="overflow-x-auto rounded-2xl border border-outline-variant/20 bg-surface-container-lowest shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-outline-variant/15 bg-surface-container-low text-xs uppercase tracking-wide text-on-surface-variant">
            <tr>
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3">Téléphone</th>
              <th className="px-4 py-3">Catégorie</th>
              <th className="px-4 py-3">Raison catégorisation</th>
              <th className="px-4 py-3">Inscrit le</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-on-surface-variant">
                  Aucun apprenant avec paiement enregistré.
                </td>
              </tr>
            ) : (
              paginatedItems.map((row) => (
                <tr key={row.id} className="border-b border-outline-variant/10 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-900">{formatName(row)}</td>
                  <td className="px-4 py-3">{row.telephone || '—'}</td>
                  <td className="px-4 py-3">{row.profilApprenant?.categorie || '—'}</td>
                  <td className="px-4 py-3 text-xs text-on-surface-variant">{row.categorisationRaison || '—'}</td>
                  <td className="px-4 py-3 text-on-surface-variant">
                    {row.dateCreation ? new Date(row.dateCreation).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Link
                        to={`/admin/apprenants/${row.id}`}
                        state={{ apprenantLabel: formatName(row) }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary transition hover:bg-primary/10"
                        title="Voir la fiche complète"
                      >
                        <LucideSymbolIcon name="visibility" tone="inherit" size={16} />
                        Détails
                      </Link>
                      <AdminTableDeleteButton
                        canDelete={canDelete}
                        disabled={deletingId === row.id}
                        confirmMessage={`Supprimer définitivement ${formatName(row)} et toutes ses données associées ? Cette action est irréversible.`}
                        onDelete={async () => {
                          setDeletingId(row.id);
                          try {
                            await deleteAdminApprenant(row.id);
                            onRefresh();
                          } finally {
                            setDeletingId(null);
                          }
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {items.length > 0 ? (
          <AdminPagination
            className="border-t border-outline-variant/15 px-4 py-3"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            summary={summary}
          />
        ) : null}
      </div>
    </AdminPageShell>
  );
}
